import React, { useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Camera, User, Phone, Car, Home, FileText, Upload, QrCode, X, CheckCircle2, XCircle } from 'lucide-react';

const PURPOSE_PRESETS = [
  'Food Delivery (Swiggy, Zomato etc)',
  'Courier (DTDC, Amazon etc)',
  'Cab',
  'Groceries',
];

function VillaMultiInput({ villas, onChange, legacyValue }) {
  // Migrates the legacy single-villa string into the new chips list the
  // first time the form is rendered, so existing parent state still works.
  const [draft, setDraft] = useState('');
  useEffect(() => {
    if ((villas?.length ?? 0) === 0 && legacyValue && legacyValue.trim()) {
      onChange([legacyValue.trim()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addVilla = (raw) => {
    const v = (raw || '').trim();
    if (!v) return;
    const exists = villas.some((x) => x.toLowerCase() === v.toLowerCase());
    if (exists) return;
    onChange([...villas, v]);
    setDraft('');
  };

  const removeVilla = (idx) => onChange(villas.filter((_, i) => i !== idx));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addVilla(draft);
    } else if (e.key === 'Backspace' && !draft && villas.length > 0) {
      removeVilla(villas.length - 1);
    }
  };

  return (
    <div
      className="min-h-10 px-2 py-1.5 border border-slate-300 rounded-md bg-white flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-gk-teal focus-within:ring-offset-0"
      data-testid="visitor-villa-chips"
      onClick={() => document.getElementById('visitor-villa-input-multi')?.focus()}
    >
      {villas.map((v, idx) => (
        <span
          key={`${v}-${idx}`}
          data-testid={`villa-chip-${v}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gk-teal/10 text-gk-navy text-xs font-medium border border-gk-teal/30"
        >
          {v}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); removeVilla(idx); }}
            className="text-gk-navy/60 hover:text-red-600 leading-none"
            aria-label={`Remove ${v}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        id="visitor-villa-input-multi"
        data-testid="visitor-villa-input"
        placeholder={villas.length === 0 ? 'e.g. A-101 (then Enter for next villa)' : 'Add another villa…'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addVilla(draft)}
        className="flex-1 min-w-[140px] bg-transparent outline-none text-sm py-1"
      />
    </div>
  );
}

export function VisitorFormModal({
  showVisitorForm,
  setShowVisitorForm,
  visitorForm,
  setVisitorForm,
  handleCreateVisitor,
  showCamera,
  setShowCamera,
  webcamRef,
  capture,
  capturedImage,
  handleImageUpload
}) {
  // Track whether the "Other" option is selected so the custom-text input
  // remains visible while the user types (purpose may be empty mid-typing).
  const [otherMode, setOtherMode] = useState(false);
  useEffect(() => {
    if (visitorForm.purpose && !PURPOSE_PRESETS.includes(visitorForm.purpose)) {
      setOtherMode(true);
    } else if (PURPOSE_PRESETS.includes(visitorForm.purpose)) {
      setOtherMode(false);
    }
  }, [visitorForm.purpose]);

  const selectedPurpose = otherMode
    ? 'Other'
    : (PURPOSE_PRESETS.includes(visitorForm.purpose) ? visitorForm.purpose : '');

  return (
    <>
      <Dialog open={showVisitorForm} onOpenChange={setShowVisitorForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register Visitor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="visitor-name">
                  <User className="w-4 h-4 inline mr-1" />Visitor Name *
                </Label>
                <Input
                  id="visitor-name"
                  data-testid="visitor-name-input"
                  placeholder="Full name"
                  value={visitorForm.name}
                  onChange={(e) => setVisitorForm({ ...visitorForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="visitor-phone">
                  <Phone className="w-4 h-4 inline mr-1" />Phone Number *
                </Label>
                <Input
                  id="visitor-phone"
                  data-testid="visitor-phone-input"
                  placeholder="Phone number"
                  value={visitorForm.phone}
                  onChange={(e) => setVisitorForm({ ...visitorForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="visitor-plate">
                  <Car className="w-4 h-4 inline mr-1" />Vehicle Plate
                </Label>
                <Input
                  id="visitor-plate"
                  data-testid="visitor-plate-input"
                  placeholder="Optional"
                  value={visitorForm.plate_number}
                  onChange={(e) => setVisitorForm({ ...visitorForm, plate_number: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="visitor-villa">
                  <Home className="w-4 h-4 inline mr-1" />Visiting Villa(s) *
                </Label>
                <VillaMultiInput
                  villas={visitorForm.villa_numbers || []}
                  onChange={(list) => setVisitorForm({ ...visitorForm, villa_numbers: list })}
                  legacyValue={visitorForm.villa_number}
                />
                <p className="text-[11px] text-slate-500 leading-snug">
                  Type a villa and press <kbd className="px-1 py-0.5 text-[10px] bg-slate-100 border rounded">Enter</kbd> or <kbd className="px-1 py-0.5 text-[10px] bg-slate-100 border rounded">,</kbd> to add. Use for multi-villa visits (Amazon, courier, cab).
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="visitor-purpose-select">
                <FileText className="w-4 h-4 inline mr-1" />Purpose of Visit
              </Label>
              <select
                id="visitor-purpose-select"
                data-testid="visitor-purpose-select"
                value={selectedPurpose}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'Other') {
                    setOtherMode(true);
                    setVisitorForm({ ...visitorForm, purpose: '' });
                  } else {
                    setOtherMode(false);
                    setVisitorForm({ ...visitorForm, purpose: v });
                  }
                }}
                className="w-full h-10 px-3 text-sm border border-slate-300 rounded-md bg-white"
              >
                <option value="">Select purpose…</option>
                {PURPOSE_PRESETS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {otherMode && (
                <Input
                  id="visitor-purpose"
                  data-testid="visitor-purpose-input"
                  placeholder="Specify purpose"
                  value={visitorForm.purpose}
                  onChange={(e) => setVisitorForm({ ...visitorForm, purpose: e.target.value })}
                  className="mt-2"
                  autoFocus
                />
              )}
            </div>
            <div className="space-y-2">
              <Label><Camera className="w-4 h-4 inline mr-1" />Visitor Photo</Label>
              <div className="flex gap-2">
                <Button
                  data-testid="capture-photo-button"
                  type="button"
                  onClick={() => setShowCamera(true)}
                  variant="outline"
                  className="flex-1"
                >
                  <Camera className="w-4 h-4 mr-2" />Capture Photo
                </Button>
                <label className="flex-1">
                  <Button
                    data-testid="upload-photo-button"
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />Upload Photo
                  </Button>
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
              </div>
              {capturedImage && (
                <div className="mt-2">
                  <img src={capturedImage} alt="Visitor" className="w-32 h-32 object-cover rounded-lg" />
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                data-testid="submit-visitor-button"
                onClick={handleCreateVisitor}
                className="flex-1 bg-gk-teal hover:bg-gk-teal-hover"
              >
                Register & Generate Pass
              </Button>
              <Button
                data-testid="cancel-visitor-button"
                onClick={() => setShowVisitorForm(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCamera} onOpenChange={setShowCamera}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Capture Visitor Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              className="w-full rounded-lg"
            />
            <div className="flex gap-2">
              <Button onClick={capture} className="flex-1">
                <Camera className="w-4 h-4 mr-2" />Capture
              </Button>
              <Button onClick={() => setShowCamera(false)} variant="outline">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function QRScannerModal({
  showQRScanner,
  closeQRScanner,
  qrScanResult,
  qrValidating,
  setQrScanResult,
  setShowQRScanner,
  qrScannerRef,
  onQRCodeSuccess,
  onQRCodeError,
  Html5QrcodeScanner
}) {
  return (
    <Dialog open={showQRScanner} onOpenChange={(open) => !open && closeQRScanner()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Scan Gate Pass QR Code
            </div>
            <Button variant="ghost" size="sm" onClick={closeQRScanner}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!qrScanResult ? (
            <>
              {qrValidating ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gk-teal"></div>
                  <p className="mt-4 text-slate-600">Validating gate pass...</p>
                </div>
              ) : (
                <>
                  <div id="qr-reader" className="w-full"></div>
                  <p className="text-sm text-center text-slate-500">
                    Point the camera at the visitor&apos;s QR code
                  </p>
                </>
              )}
            </>
          ) : (
            <div className={`p-6 rounded-xl border-2 ${
              qrScanResult.valid ? 'bg-emerald-50 border-emerald-500' : 'bg-red-50 border-red-500'
            }`}>
              <div className="flex flex-col items-center text-center">
                {qrScanResult.valid ? (
                  <>
                    <CheckCircle2 className="w-16 h-16 text-emerald-600 mb-4" />
                    <h3 className="text-2xl font-bold text-emerald-900">ACCESS GRANTED</h3>
                    {qrScanResult.data && (
                      <div className="mt-4 space-y-2 text-left w-full">
                        <p className="text-sm">
                          <span className="text-slate-500">Visitor:</span>{' '}
                          <span className="font-semibold">{qrScanResult.data.visitor || qrScanResult.data.visitor_name}</span>
                        </p>
                        {qrScanResult.data.villa && (
                          <p className="text-sm">
                            <span className="text-slate-500">Visiting:</span>{' '}
                            <span className="font-semibold">Villa {qrScanResult.data.villa}</span>
                          </p>
                        )}
                        {qrScanResult.data.resident && (
                          <p className="text-sm">
                            <span className="text-slate-500">Host:</span>{' '}
                            <span className="font-semibold">{qrScanResult.data.resident}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <XCircle className="w-16 h-16 text-red-600 mb-4" />
                    <h3 className="text-2xl font-bold text-red-900">ACCESS DENIED</h3>
                    <p className="text-red-700 mt-2">{qrScanResult.message}</p>
                  </>
                )}
              </div>
              <div className="mt-6 flex gap-2">
                <Button
                  onClick={() => {
                    setQrScanResult(null);
                    setShowQRScanner(true);
                    setTimeout(() => {
                      if (!qrScannerRef.current) {
                        const scanner = new Html5QrcodeScanner(
                          "qr-reader",
                          { fps: 10, qrbox: { width: 250, height: 250 } },
                          false
                        );
                        scanner.render(onQRCodeSuccess, onQRCodeError);
                        qrScannerRef.current = scanner;
                      }
                    }, 100);
                  }}
                  className="flex-1"
                  variant="outline"
                >
                  Scan Another
                </Button>
                <Button
                  onClick={closeQRScanner}
                  className={qrScanResult.valid ? 'flex-1 bg-emerald-600 hover:bg-emerald-700' : 'flex-1 bg-red-600 hover:bg-red-700'}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
